import { createContext, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "bodega.lang";

export const dict = {
  en: {
    nav: {
      tagline: "/AGENCY · EST. 2012",
      services: "Services",
      work: "Work",
      process: "Process",
      contact: "Contact",
      hire: "[ Hire Us ]",
    },
    hero: {
      meta1: "[ N° 001 / CREATIVE AGENCY ]",
      meta2: "CDMX · MX → GLOBAL",
      statusLabel: "STATUS:",
      statusValue: "ACTIVE",
      mandate: "// Mandate",
      subtitle:
        "Where Brand Identity Meets High-Performance Apparel & Technical Production.",
      dossierLabel: "// Dossier",
      bioLead: "Over",
      bioYears: "13 years",
      bioMid:
        "leading creative workflows and branding strategies for global brands and elite sports leagues. We deliver comprehensive brand identities and design solutions that are",
      bioAccent: "100% production-ready.",
      cta: "[ View Credentials / Contact ]",
      scroll: "↓ Scroll · Index 01 → 06",
      methods: "Sublimation · DTF · DTG · Embroidery",
      copyright: "© 2026 / BODEGA",
    },
    services: {
      eyebrow: "// Section 01 / Services",
      titleA: "Four pillars.",
      titleB: "One workshop.",
      lede:
        "Each pillar is a discipline — built on rigour, audited against league compliance, military specs, and global corporate standards. No decoration. Only craft that ships.",
      items: [
        {
          title: "Brand Identity & Compliance",
          body:
            "End-to-end branding, official style guides, and strict licensing alignment for global corporations and professional sports leagues.",
          tags: ["IDENTITY", "STYLE GUIDES", "LICENSING"],
        },
        {
          title: "Technical Apparel Design",
          body:
            "Complete lifestyle concepts for professional uniforms, high-performance sportswear, and specialized tactical gear.",
          tags: ["UNIFORMS", "PERFORMANCE", "TACTICAL"],
        },
        {
          title: "Production-Ready Artistry",
          body:
            "Expert pre-press layout for Sublimation, DTF, DTG, and industrial Embroidery — maintaining a proven 99% accuracy rate.",
          tags: ["SUBLIMATION", "DTF", "DTG", "EMBROIDERY"],
        },
        {
          title: "Presentation Design",
          body:
            "High-impact presentation design for executive pitches, investor decks, sales enablement, and corporate keynotes — tailored to each company's brand system and audience.",
          tags: ["DECKS", "PITCH", "KEYNOTE", "CORPORATE"],
        },
      ],
    },
    cases: {
      eyebrow: "// Section 02 / Track Record",
      titleA: "Curated case studies.",
      titleB: "Files from the floor.",
      stat: "13",
      statSuffix: "+ YRS",
      lede:
        "Of production-ready output across leagues, brands, & defense.",
      labels: { client: "Client", roster: "Roster", methods: "Methods", view: "View Spec Sheet" },
      items: [
        {
          code: "CS-01",
          sector: "ELITE SPORTS · MERCHANDISING",
          title: "League-Compliant Jerseys & Drops",
          client: "AHL & NHL Teams",
          detail: "Abbotsford Canucks · San Diego Gulls · Florida Panthers",
          methods: ["Custom Sublimation", "DTF Tech Packs", "League Branding Compliance"],
          tag: "ON-ICE",
        },
        {
          code: "CS-02",
          sector: "GLOBAL CORPORATE · ACTIVATIONS",
          title: "Regional Campaigns, Global Identity",
          client: "Frito-Lay · Heineken",
          detail: "Cheetos Campaign · Heineken Company",
          methods: ["Visual Assets", "Regional Brand Activation", "Global Identity Consistency"],
          tag: "FMCG",
        },
        {
          code: "CS-03",
          sector: "DEFENSE · SPECIALIZED GEAR",
          title: "High-Demand Tactical Apparel",
          client: "US Armed Forces",
          detail: "1st Marine Division · 2nd Squad US Army",
          methods: ["High-Density Embroidery", "Technical Precision Layouts", "DTG Printing"],
          tag: "TACTICAL",
        },
        {
          code: "CS-04",
          sector: "IMPACT · ENTERTAINMENT",
          title: "Championship & Broadcast Apparel",
          client: "World Lacrosse · Special Olympics · Freevee",
          detail: "EPOCH · USA Games · Good Family Football",
          methods: ["Sublimation", "DTG", "Custom Embroidery"],
          tag: "BROADCAST",
        },
        {
          code: "CS-05",
          sector: "BRAND IDENTITY · SPORTSWEAR",
          title: "A New Player Is Coming",
          client: "Shark Sportswear",
          detail: "Custom Teamwear · Identity System · Launch Campaign",
          methods: ["Logo Design", "Identity System", "Launch Comms"],
          tag: "BRANDING",
        },
        {
          code: "CS-06",
          sector: "RESEARCH · PRESENTATION DESIGN",
          title: "Decks for Research",
          client: "Limón Casa de Investigación",
          detail: "Research Lab · CDMX · Innovación + Evaluación + Branding",
          methods: ["Presentation Design", "Editorial Layout", "Visual Storytelling"],
          tag: "DECKS",
        },
      ],
    },
    workflow: {
      eyebrow: "// Section 03 / Workflow",
      titleA: "Process",
      titleB: "as proof.",
      lede:
        "A rigorous fan-centric and corporate workflow. Four checkpoints. No ambiguity. Every deliverable is signed off against the spec sheet.",
      terminalCmd: "bodega@workshop:~ ./run-process.sh",
      compiling: "COMPILING",
      stageLabel: "STAGE",
      stageOk: "OK",
      exit: "EXIT 0 · ",
      exitTag: "PRODUCTION-READY",
      accuracy: "ACCURACY · 99%+",
      steps: [
        {
          title: "Research & Style Guide Analysis",
          body:
            "Deep dive into official identity materials and culture. Audit league/licensing terms, archive references, and color systems before any pixel moves.",
        },
        {
          title: "Blueprinting & Technical Sketching",
          body:
            "Versatile silhouettes balancing comfort and bold visual impact. Drawn against block patterns to anticipate fit, fabric stretch, and panel construction.",
        },
        {
          title: "Fabric Sourcing & Tech Packs",
          body:
            "Locking in exact graphic placement, dimensions, and stitch specifications. Vendor-ready files with measurements, threads, and tolerance windows.",
        },
        {
          title: "Production Delivery",
          body:
            "Files optimized for 100% accuracy in print and manufacturing results. Proofed across Sublimation, DTF, DTG, and Embroidery output streams.",
        },
      ],
    },
    footer: {
      eyebrow: "// Section 04 / Contact",
      claimA: "Got a project?",
      claimB: "Shape it with ",
      claimC: "BODEGA.",
      emailLabel: "Email Us",
      lineLabel: "// Direct Line",
      availLabel: "// Availability",
      briefs: "Briefs",
      briefsValue: "OPEN",
      response: "Response Time",
      responseValue: "< 24H",
      hq: "HQ · CDMX, MX",
      serving: "SERVING · GLOBAL",
      copyright: "© 2026 BODEGA Agency. All rights reserved.",
      tag: "PRODUCTION-READY · 99%+ ACCURACY",
      statusLabel: "STATUS · ",
      statusValue: "OPEN FOR BRIEFS",
      marquee: [
        "Brand Identity",
        "Apparel",
        "Sublimation",
        "DTF",
        "DTG",
        "Embroidery",
        "Trade Shows",
        "Tactical",
        "Sports",
      ],
    },
  },

  es: {
    nav: {
      tagline: "/AGENCIA · EST. 2012",
      services: "Servicios",
      work: "Proyectos",
      process: "Proceso",
      contact: "Contacto",
      hire: "[ Contrátanos ]",
    },
    hero: {
      meta1: "[ N° 001 / AGENCIA CREATIVA ]",
      meta2: "CDMX · MX → GLOBAL",
      statusLabel: "ESTADO:",
      statusValue: "ACTIVO",
      mandate: "// Mandato",
      subtitle:
        "Donde la identidad de marca se encuentra con la indumentaria de alto rendimiento y la producción técnica.",
      dossierLabel: "// Expediente",
      bioLead: "Más de",
      bioYears: "13 años",
      bioMid:
        "liderando flujos creativos y estrategias de marca para empresas globales y ligas deportivas de élite. Entregamos identidades de marca integrales y soluciones de diseño",
      bioAccent: "100% listas para producción.",
      cta: "[ Ver Credenciales / Contacto ]",
      scroll: "↓ Desliza · Índice 01 → 06",
      methods: "Sublimación · DTF · DTG · Bordado",
      copyright: "© 2026 / BODEGA",
    },
    services: {
      eyebrow: "// Sección 01 / Servicios",
      titleA: "Cuatro pilares.",
      titleB: "Un taller.",
      lede:
        "Cada pilar es una disciplina — construida sobre rigor, auditada contra el cumplimiento de ligas, especificaciones militares y estándares corporativos globales. Sin decoración. Solo oficio que se entrega.",
      items: [
        {
          title: "Identidad de Marca & Cumplimiento",
          body:
            "Branding integral, manuales de estilo oficiales y alineación estricta de licencias para corporativos globales y ligas deportivas profesionales.",
          tags: ["IDENTIDAD", "MANUALES", "LICENCIAS"],
        },
        {
          title: "Diseño de Indumentaria Técnica",
          body:
            "Conceptos lifestyle completos para uniformes profesionales, ropa deportiva de alto rendimiento y equipamiento táctico especializado.",
          tags: ["UNIFORMES", "RENDIMIENTO", "TÁCTICO"],
        },
        {
          title: "Arte Listo para Producción",
          body:
            "Pre-prensa experta para Sublimación, DTF, DTG y Bordado industrial — manteniendo un 99% de precisión comprobado.",
          tags: ["SUBLIMACIÓN", "DTF", "DTG", "BORDADO"],
        },
        {
          title: "Diseño de Presentaciones",
          body:
            "Presentaciones de alto impacto para pitches ejecutivos, decks de inversión, materiales de ventas y keynotes corporativos — a la medida del sistema de marca y audiencia de cada empresa.",
          tags: ["DECKS", "PITCH", "KEYNOTE", "CORPORATIVO"],
        },
      ],
    },
    cases: {
      eyebrow: "// Sección 02 / Trayectoria",
      titleA: "Casos seleccionados.",
      titleB: "Archivos del taller.",
      stat: "13",
      statSuffix: "+ AÑOS",
      lede:
        "De producción lista para entrega en ligas, marcas y defensa.",
      labels: { client: "Cliente", roster: "Roster", methods: "Métodos", view: "Ver Ficha Técnica" },
      items: [
        {
          code: "CS-01",
          sector: "DEPORTE ÉLITE · MERCHANDISING",
          title: "Jerseys & Drops con Cumplimiento de Liga",
          client: "Equipos AHL & NHL",
          detail: "Abbotsford Canucks · San Diego Gulls · Florida Panthers",
          methods: ["Sublimación Custom", "Tech Packs DTF", "Cumplimiento de Marca de Liga"],
          tag: "EN-HIELO",
        },
        {
          code: "CS-02",
          sector: "CORPORATIVO GLOBAL · ACTIVACIONES",
          title: "Campañas Regionales, Identidad Global",
          client: "Frito-Lay · Heineken",
          detail: "Campaña Cheetos · Heineken Company",
          methods: ["Activos Visuales", "Activación Regional de Marca", "Consistencia de Identidad Global"],
          tag: "FMCG",
        },
        {
          code: "CS-03",
          sector: "DEFENSA · EQUIPO ESPECIALIZADO",
          title: "Indumentaria Táctica de Alta Exigencia",
          client: "Fuerzas Armadas de EE. UU.",
          detail: "1st Marine Division · 2nd Squad US Army",
          methods: ["Bordado de Alta Densidad", "Layouts de Precisión Técnica", "Impresión DTG"],
          tag: "TÁCTICO",
        },
        {
          code: "CS-04",
          sector: "IMPACTO · ENTRETENIMIENTO",
          title: "Indumentaria de Campeonato & Broadcast",
          client: "World Lacrosse · Special Olympics · Freevee",
          detail: "EPOCH · USA Games · Good Family Football",
          methods: ["Sublimación", "DTG", "Bordado a Medida"],
          tag: "BROADCAST",
        },
        {
          code: "CS-05",
          sector: "IDENTIDAD DE MARCA · SPORTSWEAR",
          title: "Un Nuevo Jugador Está Llegando",
          client: "Shark Sportswear",
          detail: "Teamwear a Medida · Sistema de Identidad · Campaña de Lanzamiento",
          methods: ["Diseño de Logo", "Sistema de Identidad", "Comunicación de Lanzamiento"],
          tag: "BRANDING",
        },
        {
          code: "CS-06",
          sector: "INVESTIGACIÓN · DISEÑO DE PRESENTACIONES",
          title: "Decks para Investigación",
          client: "Limón Casa de Investigación",
          detail: "Laboratorio · CDMX · Innovación + Evaluación + Branding",
          methods: ["Diseño de Presentaciones", "Diseño Editorial", "Narrativa Visual"],
          tag: "DECKS",
        },
      ],
    },
    workflow: {
      eyebrow: "// Sección 03 / Proceso",
      titleA: "Proceso",
      titleB: "como prueba.",
      lede:
        "Un flujo riguroso centrado en el fan y el cliente corporativo. Cuatro puntos de control. Sin ambigüedad. Cada entregable se firma contra la hoja de especificación.",
      terminalCmd: "bodega@workshop:~ ./run-process.sh",
      compiling: "COMPILANDO",
      stageLabel: "ETAPA",
      stageOk: "OK",
      exit: "EXIT 0 · ",
      exitTag: "LISTO PARA PRODUCCIÓN",
      accuracy: "PRECISIÓN · 99%+",
      steps: [
        {
          title: "Investigación & Análisis de Manual",
          body:
            "Inmersión profunda en materiales de identidad oficial y cultura. Auditoría de términos de licencia, referencias de archivo y sistemas de color antes de mover un pixel.",
        },
        {
          title: "Planos & Bocetaje Técnico",
          body:
            "Siluetas versátiles que equilibran comodidad e impacto visual fuerte. Dibujadas sobre patrones base para anticipar fit, elasticidad de tela y construcción de paneles.",
        },
        {
          title: "Tela & Tech Packs",
          body:
            "Fijando la colocación gráfica exacta, dimensiones y especificaciones de costura. Archivos listos para proveedor con medidas, hilos y tolerancias.",
        },
        {
          title: "Entrega de Producción",
          body:
            "Archivos optimizados para 100% de precisión en impresión y manufactura. Probados en Sublimación, DTF, DTG y Bordado.",
        },
      ],
    },
    footer: {
      eyebrow: "// Sección 04 / Contacto",
      claimA: "¿Tienes un proyecto?",
      claimB: "Dale forma con ",
      claimC: "BODEGA.",
      emailLabel: "Escríbenos",
      lineLabel: "// Línea Directa",
      availLabel: "// Disponibilidad",
      briefs: "Briefs",
      briefsValue: "ABIERTOS",
      response: "Tiempo de Respuesta",
      responseValue: "< 24H",
      hq: "HQ · CDMX, MX",
      serving: "ALCANCE · GLOBAL",
      copyright: "© 2026 BODEGA Agency. Todos los derechos reservados.",
      tag: "LISTO PARA PRODUCCIÓN · 99%+ PRECISIÓN",
      statusLabel: "ESTADO · ",
      statusValue: "ABIERTO A BRIEFS",
      marquee: [
        "Identidad de Marca",
        "Indumentaria",
        "Sublimación",
        "DTF",
        "DTG",
        "Bordado",
        "Trade Shows",
        "Táctico",
        "Deporte",
      ],
    },
  },
};

const LanguageContext = createContext({ lang: "en", setLang: () => {}, t: dict.en });

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState("en");

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "en" || saved === "es") setLangState(saved);
    } catch (_) {}
  }, []);

  const setLang = (next) => {
    setLangState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_) {}
  };

  const value = { lang, setLang, t: dict[lang] };
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useT = () => useContext(LanguageContext);
